<script setup lang="ts">
/**
 * ExportDialog — Generate EFT format and copy to clipboard
 *
 * Props:
 *   visible — controls dialog visibility
 *
 * Events:
 *   close — when dialog should close
 */
import { ref, computed, watch } from 'vue';
import { useFitStore } from '../../stores/fitStore';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const store = useFitStore();
const copied = ref(false);

const eftText = computed(() => {
  const fit = store.activeFit;
  if (!fit) return '';

  const lines: string[] = [];

  // Header: [Ship Name, Fit Name]
  lines.push(`[${fit.shipName}, ${fit.fitName}]`);
  lines.push('');

  // Low slots
  const lowMods = fit.modules
    .filter(m => m.slotType === 'low')
    .sort((a, b) => a.slotPosition - b.slotPosition);
  for (const m of lowMods) {
    lines.push(m.chargeName ? `${m.typeName}, ${m.chargeName}` : m.typeName);
  }

  lines.push('');

  // Med slots
  const medMods = fit.modules
    .filter(m => m.slotType === 'med')
    .sort((a, b) => a.slotPosition - b.slotPosition);
  for (const m of medMods) {
    lines.push(m.chargeName ? `${m.typeName}, ${m.chargeName}` : m.typeName);
  }

  lines.push('');

  // High slots
  const highMods = fit.modules
    .filter(m => m.slotType === 'high')
    .sort((a, b) => a.slotPosition - b.slotPosition);
  for (const m of highMods) {
    lines.push(m.chargeName ? `${m.typeName}, ${m.chargeName}` : m.typeName);
  }

  lines.push('');

  // Rigs
  const rigMods = fit.modules
    .filter(m => m.slotType === 'rig')
    .sort((a, b) => a.slotPosition - b.slotPosition);
  for (const m of rigMods) {
    lines.push(m.typeName);
  }

  // Subsystems
  if (fit.subsystems.length > 0) {
    lines.push('');
    for (const s of fit.subsystems) {
      lines.push(s.typeName);
    }
  }

  // Drones
  if (fit.drones.length > 0) {
    lines.push('');
    lines.push('');
    for (const d of fit.drones) {
      lines.push(`${d.typeName} x${d.quantity}`);
    }
  }

  // Cargo
  if (fit.cargo.length > 0) {
    lines.push('');
    for (const c of fit.cargo) {
      lines.push(`${c.typeName} x${c.quantity}`);
    }
  }

  return lines.join('\n');
});

watch(() => props.visible, (v) => {
  if (v) {
    copied.value = false;
  }
});

async function handleCopy(): Promise<void> {
  try {
    await navigator.clipboard.writeText(eftText.value);
    copied.value = true;
    store.addToast('success', 'Copied to clipboard');
    setTimeout(() => { copied.value = false; }, 2000);
  } catch {
    store.addToast('error', 'Failed to copy to clipboard');
  }
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('close');
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
    <div v-if="visible" class="modal-overlay" @click.self="emit('close')" @keydown="handleKeydown">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3 class="modal-title">Export Fitting (EFT)</h3>
          <button class="modal-close" @click="emit('close')">&times;</button>
        </div>

        <div class="modal-body">
          <textarea
            class="eft-output"
            :value="eftText"
            rows="16"
            readonly
            spellcheck="false"
            @focus="($event.target as HTMLTextAreaElement).select()"
          />
        </div>

        <div class="modal-footer">
          <button class="btn btn--secondary" @click="emit('close')">Close</button>
          <button class="btn btn--primary" @click="handleCopy">
            {{ copied ? 'Copied!' : 'Copy to Clipboard' }}
          </button>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 480px;
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: modal-in 0.15s ease-out;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 16px;
  overflow-y: auto;
}

.eft-output {
  width: 100%;
  background: var(--bg-darker);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.78rem;
  padding: 10px;
  resize: vertical;
  line-height: 1.5;
  cursor: text;
}

.eft-output:focus {
  outline: none;
  border-color: var(--accent-blue);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
}

.btn {
  padding: 6px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn--secondary {
  background: transparent;
  color: var(--text-secondary);
}

.btn--secondary:hover {
  color: var(--text-primary);
  border-color: var(--text-secondary);
}

.btn--primary {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: #fff;
}

.btn--primary:hover {
  background: #3a8eef;
}

/* Modal transitions */
.modal-enter-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-dialog {
  animation: modal-in 0.2s ease-out;
}

.modal-leave-active {
  transition: opacity 0.15s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

@keyframes modal-in {
  from {
    transform: translateY(-8px) scale(0.97);
  }
  to {
    transform: translateY(0) scale(1);
  }
}
</style>
